/**
 * Portal Make Payment (PD-14057) — pay one or more invoices in a single
 * payment, deliberately place money on account, and spend Account Funds.
 *
 * Mirrors the CRM Make Payment reconciliation model: the Total Payment is
 * either DERIVED from the applied amounts (default) or ENTERED as an explicit
 * constraint — in entered mode any unapplied remainder must be explicitly
 * routed to the customer account before submitting (no silent on-account
 * money). Account Funds (when the tenant has it configured and a balance
 * exists) is capped at the available amount and can never route money back
 * onto the account.
 */

import { faPlus, faWallet } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	filterUsablePaymentMethods,
	formatPaymentMethodLabel,
	formatPaymentMethodOptionLabel,
	paymentMethodsEligibleForDefault,
	resolveAddPaymentMethodType,
	resolvePaymentMethodFeeRate,
} from '@supply-mover-2-0/core';
import type {
	PaymentDecline,
	PortalMakePaymentContextDto,
} from '@supply-mover-2-0/shared';
import {
	formatPaymentRestrictionMessage,
	isPartialAccountFundsSpend,
	isZeroTotalCreditPayment,
	NO_PAYMENT_RESTRICTIONS,
} from '@supply-mover-2-0/shared';
import {
	CardBrandIcon,
	extractPaymentDecline,
	PaymentDeclineModal,
	Select,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { CurrencyInput } from '../components/CurrencyInput';
import {
	OpenInvoicePicker,
	type InvoicePaymentLineModel,
} from '../components/OpenInvoicePicker';
import { PayAccountsView } from '../components/PayAccountsView';
import { PaymentSummary } from '../components/PaymentSummary';
import { PortalAddPaymentMethod } from '../components/PortalAddPaymentMethod';
import { PortalSidebar } from '../components/PortalSidebar';
import { invalidatePaymentAvailability } from '../hooks/useCanMakePayment';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { toCustomerDecline } from '../lib/customerDecline';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import { triggerLightImpact } from '../lib/haptics';
import { PAGE_CONTENT } from '../lib/pageWidth';
import * as portalPaymentService from '../services/portalPaymentService';
import { useLoginModalStore } from '../stores/loginModalStore';

const ACCOUNT_FUNDS = 'Account Funds';

/** One invoice's line, shared with OpenInvoicePicker. */
type LineStateModel = InvoicePaymentLineModel;

function round2(value: number): number {
	return Math.round(value * 100) / 100;
}

function parseAmount(raw: string): number {
	const parsed = parseFloat(raw);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

const SingleAccountMakePayment: React.FC = () => {
	const { t } = useTranslation();
	const { token, isAuthenticated } = usePortalAuth();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const openLoginModal = useLoginModalStore((s) => s.open);

	// Signed-out visitors get the login modal over an empty page rather than
	// the payment form, matching the sibling account pages (PD-14185).
	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	const [context, setContext] = useState<PortalMakePaymentContextDto | null>(
		null,
	);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);

	const [lines, setLines] = useState<Record<number, LineStateModel>>({});
	/** Raw user-typed Total Payment. '' = derived mode. */
	const [totalInput, setTotalInput] = useState('');
	/** Explicit money placed on the customer account. */
	const [onAccount, setOnAccount] = useState('');
	/**
	 * PD-15585 — "use my account funds" for THIS payment, the same split
	 * tender the cart offers (PD-15084). Never remembered and never on by
	 * default: the balance is only spent when the customer ticks the box
	 * beside the figure it would spend.
	 */
	const [useAccountFunds, setUseAccountFunds] = useState(false);
	const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
	const [showAddPayment, setShowAddPayment] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	// PD-15185 — a gateway decline, shown as a dialog rather than inline.
	const [decline, setDecline] = useState<PaymentDecline | null>(null);
	const [success, setSuccess] = useState(false);

	/** Saved-method ids seen on the last fetch — a refetch that surfaces a
	 *  new id means the user just added that method (PD-15014). */
	const knownMethodIdsRef = useRef<Set<number> | null>(null);

	const fetchContext = useCallback(async (): Promise<void> => {
		if (token == null) {
			return;
		}
		setLoading(true);
		setLoadError(null);
		try {
			const res = await portalPaymentService.getMakePaymentContext(token);
			if (res.success) {
				setContext(res.data);
				// PD-15014 — MERGE line state instead of re-initializing it.
				// Adding a payment method mid-flow refetches this context, and a
				// full reset silently wiped every ticked invoice and typed amount.
				setLines((prev) => {
					const next: Record<number, LineStateModel> = {};
					for (const invoice of res.data.openInvoices) {
						next[invoice.id] = prev[invoice.id] ?? {
							checked: false,
							amount: '',
						};
					}
					return next;
				});
				const methods = res.data.savedPaymentMethods;
				const prevKnown = knownMethodIdsRef.current;
				knownMethodIdsRef.current = new Set(methods.map((m) => m.id));
				// PD-16381 — the first load leaves the choice to
				// `effectiveMethodId` (the primary, else the first method). On a
				// refetch (add-payment-method sidebar closed), a method id not
				// seen before was just added — select it, since paying with it
				// is why it was added. Otherwise the user's selection stands.
				//
				// PD-17009 — among the account's own methods only. A method
				// another account started sharing since the last load is new to
				// this list too, and must not be mistaken for the one just added.
				const fresh =
					prevKnown == null
						? null
						: paymentMethodsEligibleForDefault(methods).find(
								(m) => !prevKnown.has(m.id),
							);
				if (fresh != null) {
					setSelectedMethodId(fresh.id);
				}
			}
		} catch (err) {
			setLoadError(
				err instanceof Error ? err.message : t('Failed to load payment data'),
			);
		} finally {
			setLoading(false);
		}
	}, [token, t]);

	useEffect(() => {
		void fetchContext();
	}, [fetchContext]);

	// PD-15585 — Account Funds is no longer a payment TYPE the customer picks;
	// it is a checkbox that spends the balance first and leaves the remainder
	// to the chosen method. The type row is still needed to SUBMIT a payment
	// the funds cover outright, so it is kept out of the picker, not the data.
	const accountFundsType =
		(context?.paymentTypes ?? []).find(
			(pt) => pt.paymentMethodType === ACCOUNT_FUNDS,
		) ?? null;
	const gatewayPaymentTypes = useMemo(
		() =>
			(context?.paymentTypes ?? []).filter(
				(pt) => pt.paymentMethodType !== ACCOUNT_FUNDS,
			),
		[context?.paymentTypes],
	);

	const availableFunds = context?.accountFunds.available ?? 0;
	const fundsOffered =
		(context?.accountFunds.enabled ?? false) && availableFunds > 0;
	// PD-14922 — whether money may be ADDED to the account. Off for tenants
	// whose ERP cannot record an on-account deposit. Independent of whether
	// Account Funds is usable as a payment method: the customer keeps spending
	// the balance they already hold. Defaults to allowed before the context
	// loads, so the row does not flash out from under a mid-typing customer.
	const depositAllowed = context?.accountFunds.depositAllowed ?? true;

	// PD-16381 — every saved method this account can pay with, cards and bank
	// accounts in one list; a method whose type is not offered is left out.
	const usableMethods = useMemo(
		() =>
			filterUsablePaymentMethods(
				context?.savedPaymentMethods ?? [],
				gatewayPaymentTypes,
			),
		[context?.savedPaymentMethods, gatewayPaymentTypes],
	);
	// The method actually in play, always one the picker lists: the choice
	// made, else the primary, else the first. The picker, the fee preview and
	// the submit all read this, so what is shown is what is charged.
	//
	// PD-17009 — the fallbacks pass over methods another account shares, so
	// a screen whose only options are shared ones opens with nothing chosen
	// (the picker shows its placeholder) rather than with someone else's
	// card already selected.
	const defaultCandidates = paymentMethodsEligibleForDefault(usableMethods);
	const effectiveMethodId =
		usableMethods.find((m) => m.id === selectedMethodId)?.id ??
		defaultCandidates.find((m) => m.isPrimary)?.id ??
		defaultCandidates.at(0)?.id ??
		null;

	// PD-15014 / PD-16381 — the selected method's fee rate: its type's
	// server-resolved effective rate, and only when the method may be
	// surcharged (PD-15173), else null.
	const selectedMethod = usableMethods.find((m) => m.id === effectiveMethodId);
	const feeRate =
		selectedMethod != null
			? resolvePaymentMethodFeeRate(gatewayPaymentTypes, selectedMethod)
			: null;

	// ─── Reconciliation math ─────────────────────────────────────────
	// PD-15441 — credit memos (RMAs) arrive on the same context as credit
	// rows (`isCreditMemo`). Their applied amounts SUBTRACT: the customer
	// pays the net of the selected invoices less the credit applied.
	const invoiceRows = useMemo(
		() =>
			(context?.openInvoices ?? []).filter(
				(invoice) => invoice.isCreditMemo !== true,
			),
		[context?.openInvoices],
	);
	const creditMemoRows = useMemo(
		() =>
			(context?.openInvoices ?? []).filter(
				(invoice) => invoice.isCreditMemo === true,
			),
		[context?.openInvoices],
	);

	const appliedTotal = useMemo(
		() =>
			round2(
				invoiceRows.reduce((sum, invoice) => {
					const line = lines[invoice.id] as LineStateModel | undefined;
					return sum + (line?.checked === true ? parseAmount(line.amount) : 0);
				}, 0),
			),
		[invoiceRows, lines],
	);
	/** Credit applied from checked credit memo rows, as a positive magnitude. */
	const creditApplied = useMemo(
		() =>
			round2(
				creditMemoRows.reduce((sum, memo) => {
					const line = lines[memo.id] as LineStateModel | undefined;
					return sum + (line?.checked === true ? parseAmount(line.amount) : 0);
				}, 0),
			),
		[creditMemoRows, lines],
	);
	const anyCreditChecked = useMemo(
		() =>
			creditMemoRows.some(
				(memo) =>
					(lines[memo.id] as LineStateModel | undefined)?.checked === true,
			),
		[creditMemoRows, lines],
	);
	const availableCredit = useMemo(
		() =>
			round2(
				creditMemoRows.reduce((sum, memo) => sum + memo.outstandingBalance, 0),
			),
		[creditMemoRows],
	);
	const onAccountAmount = parseAmount(onAccount);
	const derivedTotal = round2(appliedTotal - creditApplied + onAccountAmount);
	const enteredTotal = totalInput === '' ? null : parseAmount(totalInput);
	const effectiveTotal = enteredTotal ?? derivedTotal;
	/** Entered-mode remainder that has no destination yet. */
	const unapplied =
		enteredTotal != null ? round2(enteredTotal - derivedTotal) : 0;
	/**
	 * PD-15585 — what the balance would take off this payment, and whether it
	 * settles it outright (in which case no method is needed).
	 *
	 * Sized on the INVOICE net, not the payment total: an "Apply to My Account"
	 * amount is money going ONTO the balance and can never be paid for out of
	 * it (the server refuses the round trip), so including it here would
	 * promise a deduction the server will not make.
	 */
	const fundsTarget = round2(Math.max(0, appliedTotal - creditApplied));
	const fundsApplied =
		useAccountFunds && fundsTarget > 0.005
			? round2(Math.min(availableFunds, fundsTarget))
			: 0;
	const fundsCoverEverything =
		useAccountFunds &&
		fundsTarget > 0.005 &&
		availableFunds >= fundsTarget - 0.005 &&
		onAccountAmount <= 0.005;
	/** Applied credit may never exceed the invoice amounts it offsets. */
	const creditExceedsApplied = round2(appliedTotal - creditApplied) < -0.004;
	/** Server refuses mixing credit applications with an on-account deposit. */
	const creditWithDeposit = creditApplied > 0 && onAccountAmount > 0;

	// PD-15014 — the processing fee this payment will actually add, floored
	// per transaction to match the server's charge (PD-15174). Zero when the
	// selected card cannot be surcharged or Account Funds is paying.
	// PD-15441 — with credit applied, mirror the server's sizing exactly:
	// each invoice line carries its own floored fee and the credit relieves
	// the added fee (floored, capped) — a pooled floor over the net can read
	// a cent under what the card is actually charged.
	const feeAmount = useMemo(() => {
		if (feeRate == null || effectiveTotal <= 0) {
			return 0;
		}
		// PD-15585 — Account Funds carries no fee (PD-15082), so the rate falls
		// only on the share the method actually collects. Nothing left for it to
		// collect means no fee at all.
		if (effectiveTotal - fundsApplied <= 0.005) {
			return 0;
		}
		if (feeRate.processingFeeType !== '%') {
			return feeRate.processingFee;
		}
		if (creditApplied > 0) {
			const newFees = invoiceRows.reduce((sum, invoice) => {
				const line = lines[invoice.id] as LineStateModel | undefined;
				// PD-15688 — an invoice whose balance already embeds a fee is
				// charged as-is by the server (PD-15473), so it contributes no
				// new fee. Mirrors the CRM Make Payment split.
				if (
					line == null ||
					!line.checked ||
					invoice.unpaidProcessingFee > 0.005
				) {
					return sum;
				}
				return (
					sum +
					Math.floor(parseAmount(line.amount) * feeRate.processingFee) / 100
				);
			}, 0);
			const relief = Math.min(
				Math.floor(creditApplied * feeRate.processingFee) / 100,
				newFees,
			);
			// PD-15585 — account funds relieve the added fee the same way an
			// applied credit does: both shrink what the card collects, and the
			// server sizes the fee from the post-funds, post-credit allocations.
			const fundsRelief = Math.min(
				Math.floor(fundsApplied * feeRate.processingFee) / 100,
				round2(newFees - relief),
			);
			return round2(newFees - relief - fundsRelief);
		}
		// PD-15688 — only the portion NOT already carrying a fee is surcharged.
		const feeBearingApplied = invoiceRows.reduce((sum, invoice) => {
			const line = lines[invoice.id] as LineStateModel | undefined;
			if (
				line == null ||
				!line.checked ||
				invoice.unpaidProcessingFee <= 0.005
			) {
				return sum;
			}
			return sum + parseAmount(line.amount);
		}, 0);
		const surchargeable = Math.max(
			0,
			effectiveTotal - feeBearingApplied - fundsApplied,
		);
		return Math.floor(surchargeable * feeRate.processingFee) / 100;
	}, [
		feeRate,
		effectiveTotal,
		creditApplied,
		fundsApplied,
		invoiceRows,
		lines,
	]);
	const totalWithFee = round2(effectiveTotal + feeAmount);

	// PD-15585 — money added to the account cannot also be paid FOR out of the
	// account; the server refuses the round trip.
	const accountFundsSelfDeposit = useAccountFunds && onAccountAmount > 0;
	// PD-15585 — two shapes the tenant's ERP may refuse, named before Pay is
	// pressed with the tenant's own wording (the server refuses them the same
	// way): Account Funds for less than the whole balance, and a $0 total
	// that applied credit fully covers. Nothing blocked until the context
	// says so.
	const restrictions = context?.paymentRestrictions ?? NO_PAYMENT_RESTRICTIONS;
	const partialFundsBlocked =
		restrictions.partialAccountFundsSpend.blocked &&
		useAccountFunds &&
		isPartialAccountFundsSpend(fundsTarget, availableFunds);
	const partialFundsMessage = formatPaymentRestrictionMessage(
		restrictions.partialAccountFundsSpend.message,
		formatCurrency(availableFunds) ?? '',
	);
	const zeroTotalBlocked =
		restrictions.zeroTotalPayment.blocked &&
		isZeroTotalCreditPayment(effectiveTotal, creditApplied);
	const zeroTotalMessage = restrictions.zeroTotalPayment.message;
	const overApplied = unapplied < -0.005;
	const hasUnapplied = unapplied > 0.005;

	const lineErrors = useMemo(() => {
		const errors: Record<number, string> = {};
		for (const invoice of invoiceRows) {
			const line = lines[invoice.id] as LineStateModel | undefined;
			if (line == null || !line.checked) {
				continue;
			}
			const amount = parseAmount(line.amount);
			if (amount > invoice.outstandingBalance + 0.005) {
				errors[invoice.id] = t('Exceeds outstanding balance');
			}
		}
		for (const memo of creditMemoRows) {
			const line = lines[memo.id] as LineStateModel | undefined;
			if (line == null || !line.checked) {
				continue;
			}
			const amount = parseAmount(line.amount);
			if (amount > memo.outstandingBalance + 0.005) {
				errors[memo.id] = t('Exceeds remaining credit');
			}
		}
		return errors;
	}, [invoiceRows, creditMemoRows, lines, t]);

	// PD-14172 / PD-16674 — filtering and select-all live in OpenInvoicePicker,
	// shared with the public statement payment link.
	const hasOpenInvoices = invoiceRows.length > 0;

	const canSubmit =
		!submitting &&
		// PD-15441 — $0 is submittable when applied credit fully covers the
		// selection (record-the-applications payment). Negative nets and
		// credit-only selections stay blocked.
		effectiveTotal >= 0 &&
		(effectiveTotal > 0 || creditApplied > 0) &&
		!creditExceedsApplied &&
		!creditWithDeposit &&
		!hasUnapplied &&
		!overApplied &&
		Object.keys(lineErrors).length === 0 &&
		!accountFundsSelfDeposit &&
		!partialFundsBlocked &&
		!zeroTotalBlocked &&
		// PD-15585 — a payment the balance settles outright needs no method;
		// anything the method still has to collect needs one.
		(fundsCoverEverything || effectiveMethodId != null);

	/**
	 * The screen as it first renders, before the customer has chosen
	 * anything. Nothing is wrong here — there is simply nothing to pay yet —
	 * so the reason panel stays away and the Pay button is quietly inert.
	 * Reasons appear once a choice has been made and something about it
	 * needs correcting.
	 */
	const nothingChosenYet =
		!Object.values(lines).some((line) => line.checked) &&
		onAccountAmount === 0 &&
		enteredTotal == null &&
		!useAccountFunds;

	/**
	 * PD-15585 — every reason Pay is greyed out, collected once so the panel
	 * above the button and the guards in `canSubmit` cannot drift apart.
	 */
	const blockingReasons: Array<string> = [];
	if (effectiveTotal < 0) {
		blockingReasons.push(
			'Applied credit exceeds the selected invoice amounts. Reduce the credit applied or select more invoices.',
		);
	} else if (effectiveTotal === 0 && creditApplied === 0) {
		blockingReasons.push('Select at least one invoice to pay.');
	}
	if (creditExceedsApplied) {
		blockingReasons.push(
			'Applied credit exceeds the selected invoice amounts. Reduce the credit applied or select more invoices.',
		);
	}
	if (creditWithDeposit) {
		blockingReasons.push(
			'Credit memos cannot be combined with an Apply to My Account amount in the same payment.',
		);
	}
	if (hasUnapplied) {
		blockingReasons.push(
			'Part of the total you entered is not applied to anything yet.',
		);
	}
	if (overApplied) {
		blockingReasons.push(
			'The amounts above exceed the total payment. Reduce them or raise the total.',
		);
	}
	if (Object.keys(lineErrors).length > 0) {
		blockingReasons.push('One or more amounts above exceed what is owed.');
	}
	if (accountFundsSelfDeposit) {
		blockingReasons.push(
			'Account funds cannot pay for money added to the same account — clear the Apply to My Account amount.',
		);
	}
	if (partialFundsBlocked) {
		blockingReasons.push(partialFundsMessage);
	}
	if (zeroTotalBlocked) {
		blockingReasons.push(zeroTotalMessage);
	}
	if (!fundsCoverEverything && effectiveMethodId == null) {
		blockingReasons.push('Choose a payment method to collect the remainder.');
	}

	// ─── Handlers ────────────────────────────────────────────────────
	/**
	 * PD-15441 — toggling a credit memo row. Credit application always runs
	 * in derived mode (the net total follows the rows), and the fill is
	 * capped at what the currently selected invoices can absorb, so checking
	 * a credit never nets below zero on its own.
	 */
	const toggleCreditMemo = (memoId: number, remainingCredit: number): void => {
		setTotalInput('');
		setLines((prev) => {
			const current = prev[memoId] ?? { checked: false, amount: '' };
			if (current.checked) {
				return {
					...prev,
					[memoId]: { checked: false, amount: '' },
				};
			}
			const selectedInvoiceSum = invoiceRows.reduce((sum, invoice) => {
				const line = prev[invoice.id] as LineStateModel | undefined;
				return line?.checked === true ? sum + parseAmount(line.amount) : sum;
			}, 0);
			const otherCreditsApplied = creditMemoRows.reduce((sum, other) => {
				if (other.id === memoId) {
					return sum;
				}
				const line = prev[other.id] as LineStateModel | undefined;
				return line?.checked === true ? sum + parseAmount(line.amount) : sum;
			}, 0);
			const absorbable = Math.max(
				0,
				round2(selectedInvoiceSum - otherCreditsApplied),
			);
			const fill = Math.min(remainingCredit, absorbable);
			return {
				...prev,
				[memoId]: {
					checked: true,
					amount: fill > 0 ? fill.toFixed(2) : '',
				},
			};
		});
		setSubmitError(null);
	};

	const setLineAmount = (invoiceId: number, amount: string): void => {
		setLines((prev) => ({
			...prev,
			[invoiceId]: { checked: true, amount },
		}));
		setSubmitError(null);
	};

	const applyUnappliedToAccount = (): void => {
		setOnAccount(round2(onAccountAmount + unapplied).toFixed(2));
	};

	const handleSubmit = async (): Promise<void> => {
		if (token == null || !canSubmit || context == null) {
			return;
		}
		setSubmitting(true);
		setSubmitError(null);
		try {
			const allocations = [
				...invoiceRows
					.filter((invoice) => {
						const line = lines[invoice.id] as LineStateModel | undefined;
						return line != null && line.checked && parseAmount(line.amount) > 0;
					})
					.map((invoice) => ({
						invoiceId: invoice.id,
						amount: parseAmount(lines[invoice.id].amount),
					})),
				// PD-15441 — credit memo applications go up NEGATIVE: the memo's
				// balance offsets the invoices and only the net is charged.
				...creditMemoRows
					.filter((memo) => {
						const line = lines[memo.id] as LineStateModel | undefined;
						return line != null && line.checked && parseAmount(line.amount) > 0;
					})
					.map((memo) => ({
						invoiceId: memo.id,
						amount: -parseAmount(lines[memo.id].amount),
					})),
			];

			// PD-15585 — full cover submits as a plain Account Funds payment; a
			// balance that only covers part rides the applyAccountFunds flag
			// alongside the chosen method, which collects the remainder. Same
			// shape the cart submits (PD-15084).
			const res = await portalPaymentService.makePayment(token, {
				paymentAmount: effectiveTotal,
				invoiceAllocations: allocations,
				onAccountAmount,
				...(fundsCoverEverything
					? { paymentMethodType: ACCOUNT_FUNDS }
					: {
							paymentMethodId: effectiveMethodId ?? undefined,
							...(useAccountFunds ? { applyAccountFunds: true } : {}),
						}),
			});
			if (res.success) {
				// PD-14695: a light tap confirms the payment landed.
				triggerLightImpact();
				// PD-16789 — the last open invoice may now be paid.
				void invalidatePaymentAvailability(queryClient);
				setSuccess(true);
				window.setTimeout(() => {
					void navigate('/invoices');
				}, 1800);
			}
		} catch (err) {
			// PD-15185 — a refusal from the gateway is shown as a dialog the
			// customer has to acknowledge, with the reason, rather than a line
			// of text they may not notice above the button they just pressed.
			const paymentDecline = toCustomerDecline(extractPaymentDecline(err));

			if (paymentDecline != null) {
				setDecline(paymentDecline);
			} else {
				setSubmitError(
					err instanceof Error
						? err.message
						: t('Payment failed. Please try again.'),
				);
			}
		} finally {
			setSubmitting(false);
		}
	};

	// ─── Render ──────────────────────────────────────────────────────
	if (!isAuthenticated) {
		return <div />;
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center py-16">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-portal-accent" />
			</div>
		);
	}

	if (loadError != null) {
		return (
			<div className="p-4">
				<div className="p-4 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
					<p className="text-sm text-danger">{t(loadError)}</p>
				</div>
			</div>
		);
	}

	if (success) {
		return (
			<div className="flex flex-col items-center justify-center py-16 gap-3">
				<p className="text-lg font-bold text-default">
					{t('Payment submitted')}
				</p>
				<p className="text-sm text-brand-muted">
					{t('Returning to your invoices...')}
				</p>
			</div>
		);
	}

	return (
		<div className={`px-4 py-4 ${PAGE_CONTENT}`}>
			<h1 className="text-lg md:text-xl font-bold text-default mb-1">
				{t('Make a Payment')}
			</h1>
			{/* PD-16544 — only mention adding money when the tenant allows a
			    deposit; the "Apply to My Account" row below is gated the same
			    way, so the copy never offers something the page cannot do. */}
			<p className="text-sm text-brand-muted mb-4">
				{depositAllowed
					? t('Select invoices to pay, or add money to your account.')
					: t('Select invoices to pay.')}
			</p>

			{/* No "Account funds available" banner here — the balance already
			    shows on the Account Funds option in Payment Method below;
			    saying it twice on one screen was noise. */}

			{/* Open invoices */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<h2 className="text-sm font-semibold text-default mb-3">
					{t('Open Invoices')}
				</h2>

				{!hasOpenInvoices ? (
					<p className="text-sm text-brand-muted">
						{depositAllowed
							? t('No open invoices. You can still add money to your account.')
							: t('No open invoices on this account.')}
					</p>
				) : (
					<>
						<OpenInvoicePicker
							invoices={invoiceRows}
							lines={lines}
							onLinesChange={(update) => {
								setLines(update);
								setSubmitError(null);
							}}
							lineErrors={lineErrors}
						/>
					</>
				)}

				{/* PD-15441 — credit memos (RMAs): applied credit reduces what is
				    charged for the invoices above. Rows only arrive when the
				    tenant has payments.pay_invoices_with_credit_memos enabled. */}
				{creditMemoRows.length > 0 && (
					<div className="mt-3 border-t border-brand-border pt-3">
						<div className="flex items-center justify-between mb-1">
							<h3 className="text-sm font-semibold text-default">
								{t('Credit Memos')}
							</h3>
							<p className="text-xs text-brand-muted">
								{t('Available credit')}:{' '}
								{t(formatCurrency(availableCredit) ?? '')}
							</p>
						</div>
						<p className="text-xs text-brand-muted mb-2">
							{t(
								'Applied credit reduces the amount charged for the invoices above.',
							)}
						</p>
						<div className="space-y-2">
							{creditMemoRows.map((memo) => {
								const line = lines[memo.id] ?? {
									checked: false,
									amount: '',
								};
								return (
									<div
										key={memo.id}
										className="flex flex-wrap items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-brand-input"
									>
										<input
											type="checkbox"
											checked={line.checked}
											onChange={() => {
												toggleCreditMemo(memo.id, memo.outstandingBalance);
											}}
											className="w-4 h-4 text-portal-accent"
										/>
										<div className="flex-1 min-w-[120px]">
											<p className="text-sm font-medium text-default">
												{t('Credit Memo')} #{t(memo.invoiceNumber)}
											</p>
											<p className="text-xs text-brand-muted">
												{t('Remaining credit')}:{' '}
												{t(formatCurrency(memo.outstandingBalance) ?? '')}
												{memo.date != null && (
													<>
														{' · '}
														{t('Dated')} {t(formatStatementDate(memo.date))}
													</>
												)}
											</p>
										</div>
										<div className="w-32">
											<CurrencyInput
												value={line.amount}
												disabled={!line.checked}
												prefix="-$"
												onChange={(next) => {
													setLineAmount(memo.id, next);
												}}
												className="text-right disabled:opacity-70"
											/>
										</div>
										{(lineErrors[memo.id] as string | undefined) != null && (
											<p className="w-full text-xs text-danger text-right">
												{t(lineErrors[memo.id])}
											</p>
										)}
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* On-account row — hidden when the tenant does not allow adding
				    money to the account (PD-14922). Spending existing funds is
				    unaffected: Account Funds stays selectable below. */}
				{depositAllowed && (
					<div className="flex flex-wrap items-center gap-3 p-2 -mx-2 mt-3 border-t border-brand-border pt-3">
						<div className="flex-1 min-w-[120px]">
							<p className="text-sm font-medium text-default">
								{t('Apply to My Account')}
							</p>
							<p className="text-xs text-brand-muted">
								{t(
									'Funds added to your account balance instead of an invoice.',
								)}
							</p>
						</div>
						<div className="w-32">
							<CurrencyInput
								value={onAccount}
								disabled={fundsCoverEverything}
								onChange={(next) => {
									setOnAccount(next);
									setSubmitError(null);
								}}
								className="text-right disabled:opacity-70"
							/>
						</div>
					</div>
				)}
			</div>

			{/* Totals + reconciliation */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<div className="flex flex-wrap items-center gap-3">
					<div className="flex-1 min-w-[140px]">
						<label className="block text-sm font-medium text-strong">
							{t('Total Payment')}
						</label>
						<p className="text-xs text-brand-muted">
							{anyCreditChecked
								? t(
										'Calculated from the amounts above, less the applied credit.',
									)
								: enteredTotal == null
									? t('Calculated from the amounts above.')
									: t('Entered amount — applied amounts must match it.')}
						</p>
						{/* PD-15688 — the applied credit is stated as its own deducted
						    line in the summary below, so the sentence that used to sit
						    here (which read as though the credit were INCLUDED in this
						    figure rather than taken off it) is gone. */}
					</div>
					<div className="w-36">
						{/* An empty override means "use the calculated total", so
						    clearing the field is the same as the button beside it. */}
						<CurrencyInput
							value={totalInput === '' ? derivedTotal.toFixed(2) : totalInput}
							disabled={anyCreditChecked}
							onChange={(next) => {
								setTotalInput(next);
								setSubmitError(null);
							}}
							className="text-right font-semibold disabled:opacity-70"
						/>
					</div>
					{enteredTotal != null && (
						<button
							type="button"
							onClick={() => {
								setTotalInput('');
							}}
							className="text-xs font-medium text-portal-accent hover:underline"
						>
							{t('Use calculated total')}
						</button>
					)}
				</div>

				{hasUnapplied && (
					<div className="mt-3 p-3 rounded-lg flex flex-wrap items-center gap-2 bg-warning-badge text-warning-tint-fg border border-warning-tint">
						<p className="text-sm text-warning flex-1">
							{t('{{amount}} of your payment is not applied to anything yet.', {
								amount: formatCurrency(unapplied) ?? '',
							})}
						</p>
						{!fundsCoverEverything && depositAllowed && (
							<button
								type="button"
								onClick={applyUnappliedToAccount}
								className="text-xs font-medium px-3 py-1.5 bg-portal-accent text-white rounded-lg"
							>
								{t('Apply {{amount}} to my account', {
									amount: formatCurrency(unapplied) ?? '',
								})}
							</button>
						)}
					</div>
				)}
				{overApplied && (
					<p className="mt-3 text-sm text-danger">
						{t(
							'The amounts above exceed the total payment. Reduce them or raise the total.',
						)}
					</p>
				)}
				{creditExceedsApplied && (
					<p className="mt-3 text-sm text-danger">
						{t(
							'Applied credit exceeds the selected invoice amounts. Reduce the credit applied or select more invoices.',
						)}
					</p>
				)}
				{creditWithDeposit && (
					<p className="mt-3 text-sm text-danger">
						{t(
							'Credit memos cannot be combined with an Apply to My Account amount in the same payment.',
						)}
					</p>
				)}
			</div>

			{/* Payment method */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<h2 className="text-sm font-semibold text-default mb-3">
					{t('Payment Method')}
				</h2>
				{/* PD-15585 — the account-funds opt-in, above the method picker because it
				    decides how much of the payment they are being asked to collect
				    and, when the balance covers the whole thing, whether a method is
				    needed at all. */}
				{fundsOffered && accountFundsType != null && (
					<div className="mb-3 pb-3 border-b border-brand-border">
						<label className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-brand-input">
							<input
								type="checkbox"
								checked={useAccountFunds}
								onChange={(e) => {
									setUseAccountFunds(e.target.checked);
									setSubmitError(null);
								}}
								className="w-4 h-4 text-portal-accent"
							/>
							<FontAwesomeIcon icon={faWallet} className="text-default" />
							<span className="text-sm text-default">
								{t('Use my account funds')}
							</span>
							<span className="text-xs text-brand-muted ml-auto">
								{t(formatCurrency(availableFunds) ?? '')} {t('available')}
							</span>
						</label>
						{useAccountFunds && partialFundsBlocked && (
							<p className="ml-9 text-xs text-danger mt-1">
								{t(partialFundsMessage)}
							</p>
						)}
						{useAccountFunds && !partialFundsBlocked && effectiveTotal > 0 && (
							<p className="ml-9 text-xs text-brand-muted mt-1">
								{fundsCoverEverything
									? t(
											'Your account funds cover this payment — no other payment method is needed.',
										)
									: `${t('Applies')} ${formatCurrency(fundsApplied) ?? ''} ${t('now; the remaining')} ${formatCurrency(round2(totalWithFee - fundsApplied)) ?? ''} ${t('is collected from the payment method selected below.')}`}
							</p>
						)}
					</div>
				)}
				{/* PD-16381 — one picker of every saved method this account can
				    pay with, each priced in its label, in place of a payment-type
				    radio list with a per-type picker under it. Nothing to choose when
				    the account's funds settle the payment. */}
				{!fundsCoverEverything && gatewayPaymentTypes.length > 0 && (
					<div>
						{usableMethods.length > 0 ? (
							// PD-15878 — the shared Select, so each option carries its network's mark.
							<Select
								aria-label={t('Payment Method')}
								placeholder={t('Choose a payment method')}
								value={effectiveMethodId ?? ''}
								onChange={(next) => {
									setSelectedMethodId(next === '' ? null : parseInt(next, 10));
									setSubmitError(null);
								}}
								options={usableMethods.map((m) => {
									// PD-15011 — the rate rides in the option, "No fee" included.
									// PD-16381 — on a second line, and beside the name once chosen.
									const option = formatPaymentMethodOptionLabel(m, t, {
										feeTypes: gatewayPaymentTypes,
									});
									return {
										value: m.id,
										label: option.primary,
										subtitle: option.secondary,
										selectedSuffix: option.fee,
										icon: (
											<CardBrandIcon
												brand={m.subtype ?? m.type}
												className="text-xl"
												title={formatPaymentMethodLabel(m, t)}
											/>
										),
									};
								})}
							/>
						) : (
							<p className="text-xs text-brand-muted py-1">
								{t('No saved payment methods yet.')}
							</p>
						)}
						<button
							type="button"
							onClick={() => {
								setShowAddPayment(true);
							}}
							className="flex items-center gap-1.5 text-xs font-medium text-portal-accent hover:underline mt-3"
						>
							<FontAwesomeIcon icon={faPlus} />
							<span>{t('Add new payment method')}</span>
						</button>
					</div>
				)}

				{accountFundsSelfDeposit && (
					<p className="mt-2 text-sm text-danger">
						{t(
							'Account funds cannot pay for money added to the same account — clear the Apply to My Account amount.',
						)}
					</p>
				)}
				{partialFundsBlocked && (
					<p className="mt-2 text-sm text-danger">{t(partialFundsMessage)}</p>
				)}
				{zeroTotalBlocked && (
					<p className="mt-2 text-sm text-danger">{t(zeroTotalMessage)}</p>
				)}

				{/* PD-15688 — the checkout summary. Applied credit is now its own
				    deducted line rather than a sentence under the total, the fee
				    follows it (it is sized on what the card actually moves), and
				    the Pay button repeats the grand total. */}
				<div className="mt-3 p-3 bg-brand-input rounded-lg">
					<PaymentSummary
						total={round2(totalWithFee - fundsApplied)}
						lines={[
							{ label: 'Applied to invoices', amount: appliedTotal },
							{
								label: 'Credit memos applied',
								amount: -creditApplied,
								hideWhenZero: true,
							},
							// PD-15585 — what the balance takes off the charge,
							// deducted as its own line so the card's share is visible
							// rather than inferred.
							{
								label: 'Account funds applied',
								amount: -fundsApplied,
								hideWhenZero: true,
							},
							{
								label: 'Applied to my account',
								amount: onAccountAmount,
								hideWhenZero: true,
							},
							// A typed total that exceeds (or falls short of) the
							// amounts applied above would otherwise leave the receipt
							// not adding up. Submission is already blocked until it
							// reconciles; the line keeps the arithmetic honest
							// meanwhile.
							{
								label: unapplied >= 0 ? 'Unapplied' : 'Over-applied',
								amount: unapplied,
								hideWhenZero: true,
							},
							{
								label: 'Processing fee',
								amount: feeAmount,
								hideWhenZero: true,
							},
						]}
					/>
				</div>
			</div>

			{submitError != null && (
				<div className="mb-4 p-3 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
					<p className="text-sm text-danger">{t(submitError)}</p>
				</div>
			)}

			{/* PD-15585 — why the button below is inert. Each reason is also
			    stated where it arises, but a customer who has scrolled past the
			    invoice rows should not have to hunt back up the page for the one
			    that is stopping them. */}
			{submitError == null &&
				!nothingChosenYet &&
				blockingReasons.length > 0 && (
					<div className="mb-4 p-3 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
						<p className="text-sm font-medium text-danger">
							{t('This payment cannot be submitted yet')}
						</p>
						<ul className="mt-1 space-y-0.5">
							{blockingReasons.map((reason) => (
								<li key={reason} className="text-sm text-danger">
									{t(reason)}
								</li>
							))}
						</ul>
					</div>
				)}

			<button
				type="button"
				onClick={() => void handleSubmit()}
				disabled={!canSubmit}
				className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-portal-accent enabled:hover:bg-portal-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch mb-6"
			>
				{submitting ? (
					<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
				) : null}
				{submitting
					? t('Processing...')
					: effectiveTotal <= 0 && creditApplied > 0
						? t('Apply Credit')
						: fundsCoverEverything
							? // PD-15585 — nothing is charged to a method, so "Pay $0.00"
								// misdescribes the button. The money is real; it comes from
								// the balance.
								t('Pay with account funds')
							: // PD-15688 — the amount the card is actually charged, fee
								// included, matching the Total charge in the summary above.
								// PD-15585 — less anything the account balance covers.
								t('Pay {{amount}}', {
									amount:
										formatCurrency(
											Math.max(0, round2(totalWithFee - fundsApplied)),
										) ?? '',
								})}
			</button>

			{/* Add payment method sidebar */}
			<PortalSidebar
				isOpen={showAddPayment}
				onClose={() => {
					setShowAddPayment(false);
				}}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={() => {
							setShowAddPayment(false);
							void fetchContext();
						}}
						onCancel={() => {
							setShowAddPayment(false);
						}}
						supportedPaymentMethods={(context?.paymentTypes ?? []).filter(
							(pt) => pt.paymentMethodType !== ACCOUNT_FUNDS,
						)}
						// PD-16381 — with no type step, the form opens on the
						// selected method's type (else a card), always one this
						// account can use; its own selector switches the rest.
						preSelectedPaymentType={resolveAddPaymentMethodType(
							selectedMethod?.type,
							gatewayPaymentTypes,
						)}
					/>
				</div>
			</PortalSidebar>

			{/* PD-15185 — a refused payment, acknowledged deliberately. */}
			<PaymentDeclineModal
				decline={decline}
				onClose={(): void => {
					setDecline(null);
				}}
			/>
		</div>
	);
};

/**
 * Users who manage more than one account get the grouped, multi-account "pay
 * across my accounts" view (PD-14190); single-account users keep the original
 * flat flow.
 */
export const MakePaymentPage: React.FC = () => {
	const { availableClients } = usePortalAuth();
	return availableClients.length > 1 ? (
		<PayAccountsView />
	) : (
		<SingleAccountMakePayment />
	);
};

export default MakePaymentPage;
